import { Html5Qrcode, Html5QrcodeSupportedFormats } from 'html5-qrcode';
import { Html5QrcodeCameraScanConfig, Html5QrcodeFullConfig } from 'html5-qrcode/esm/html5-qrcode';
import { Html5QrcodeError, Html5QrcodeResult } from 'html5-qrcode/esm/core';
import { useLayoutEffect, useState } from 'react';

export type CameraIdentifierConfig = string | MediaTrackConstraints;

export interface ScannerConfig {
  elementId: string;
  config?: Html5QrcodeFullConfig;
  onSuccessfulScan: (decodedText: string, result: Html5QrcodeResult) => void;
  onScanError: (errorMessage: string, error: Html5QrcodeError) => void;
}

const defaultConfig: Html5QrcodeFullConfig = {
  verbose: false,
  formatsToSupport: [Html5QrcodeSupportedFormats.CODE_128, Html5QrcodeSupportedFormats.QR_CODE]
};
const defaultIdentifierConfig: CameraIdentifierConfig = { facingMode: 'environment' };
const defaultCameraConfig: Html5QrcodeCameraScanConfig = { fps: 10, aspectRatio: 1 };

export default function useCodeScanner({
  elementId,
  config = defaultConfig,
  onSuccessfulScan,
  onScanError
}: ScannerConfig) {
  const [scanner, setScanner] = useState<Html5Qrcode | undefined>();

  useLayoutEffect(() => {
    const scanner = new Html5Qrcode(elementId, config);
    setScanner(scanner);
    return () => {
      if (scanner.isScanning) {
        scanner.stop();
      }
      scanner.clear();
    };
  }, [elementId]);

  return {
    scanner,
    stopScanner,
    startScanner
  };

  async function stopScanner() {
    if (scanner?.isScanning) {
      await scanner.stop();
    }
  }

  async function startScanner({
    identifierConfig = defaultIdentifierConfig,
    cameraConfig = defaultCameraConfig
  }: {
    identifierConfig?: CameraIdentifierConfig;
    cameraConfig?: Html5QrcodeCameraScanConfig;
  } = {}) {
    if (scanner && !scanner.isScanning) {
      await scanner.start(identifierConfig, cameraConfig, onSuccessfulScan, onScanError);
    }
  }
}
